import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, Sparkles, ScanLine, FileText, BarChart3, Trophy, Users, BookOpen, Shield, Crown } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';
import { usePremium } from '../hooks/usePremium';
import { getStoredPromo } from '../lib/promoLink';
import { isNativeApp } from '../lib/platform';

const PREMIUM_PERKS = [
  { icon: ScanLine, text: 'AI Scan Lineup Card (saves 3 min every game)' },
  { icon: FileText, text: 'Post-Game PDF Player Report Cards' },
  { icon: BarChart3, text: 'Spray Charts with per-player drill-down' },
  { icon: Trophy, text: 'Season Milestones & Auto-Awards' },
  { icon: Users, text: 'Unlimited teams (up to 5)' },
  { icon: BookOpen, text: 'Unlimited season history' },
  { icon: Shield, text: '14-day Pitcher Rest Calendar on every pitcher' },
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedFeature = params.get('feature');
  const { isAdmin, trialActive, subscriptionActive, trialDaysLeft, loading, refresh,
          isRecurring, cancelAtPeriodEnd, currentPeriodEnd, tier, subscriptionStatus } = usePremium();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('premium_annual');
  const [checking, setChecking] = useState(false);
  const [polling, setPolling] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null);
  const [managing, setManaging] = useState(false);  // in-flight cancel/reactivate

  // User should see plans unless they already have a paid subscription (or are admin).
  // A 7-day trial alone is NOT a reason to hide the upgrade CTA — trial users need to convert.
  const hidePlans = subscriptionActive || isAdmin;

  useEffect(() => {
    // Coach upgrade page — never list the family plan ($4.99 parent tier) here.
    // Parents use the FamilyUpsellCard / FamilyUpgradeDialog from Parent View.
    api.get('/subscription/plans')
      .then(r => setPlans((r.data.plans || []).filter(p => (p.audience || 'coach') !== 'family')))
      .catch(() => {});
  }, []);

  // Auto-prefill stored promo (from /?promo=X capture on landing/article) + validate it
  useEffect(() => {
    const stored = getStoredPromo();
    if (stored && !promoCode) {
      setPromoCode(stored);
      api.post('/auth/validate-discount', { code: stored })
        .then(res => {
          const d = res.data;
          const label = d.type === 'extended_trial'
            ? `${d.trial_days}-day trial (applies on next signup — not on this checkout)`
            : `${d.percent_off}% off ${d.type === 'first_month' ? 'first month' : d.type === 'first_year' ? 'first year' : 'every renewal'}`;
          setPromoStatus({ ok: true, summary: label, type: d.type });
        })
        .catch(() => setPromoStatus(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If returning from Stripe with ?session_id=..., poll until status is paid or timeout.
  // After confirmation we STAY on /upgrade so the user sees the renewal-date + cancel card.
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/subscription/checkout/status/${sid}`);
        if (r.data?.payment_status === 'paid' || r.data?.status === 'complete') {
          toast.success('Subscription confirmed! You\'ll be auto-charged every period until you cancel.');
          await refresh();
          setPolling(false);
          // Strip session_id from URL but stay on /upgrade so the status card is visible
          navigate('/upgrade', { replace: true });
          return;
        }
        if (r.data?.status === 'expired' || attempts > 10) {
          toast.error('Checkout expired — please try again');
          setPolling(false);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts > 10) {
          // Last-ditch: refresh status anyway — the subscription may have synced via webhook
          await refresh();
          setPolling(false);
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async () => {
    if (!selectedPlanId) return;
    setChecking(true);
    try {
      const originUrl = window.location.origin;
      const payload = {
        plan_id: selectedPlanId,
        origin_url: originUrl,
      };
      if (promoStatus?.ok && promoCode.trim()) payload.discount_code = promoCode.trim().toUpperCase();
      const r = await api.post('/subscription/checkout-recurring', payload);
      const checkoutUrl = r.data?.checkout_url || r.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setChecking(false);
    }
  };

  const validatePromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { setPromoStatus(null); return; }
    try {
      const res = await api.post('/auth/validate-discount', { code });
      const d = res.data;
      const label = d.type === 'extended_trial'
        ? `${d.trial_days}-day trial (applies on next signup — not on this checkout)`
        : `${d.percent_off}% off ${d.type === 'first_month' ? 'first month' : d.type === 'first_year' ? 'first year' : 'every renewal'}`;
      setPromoStatus({ ok: true, summary: label, type: d.type });
    } catch (err) {
      setPromoStatus({ ok: false, summary: err.response?.data?.detail || 'Invalid code' });
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription?\n\nYou'll keep premium access through your current billing period. You won't be charged again — and you can reactivate anytime before then.")) return;
    setManaging(true);
    try {
      const r = await api.post('/subscription/cancel');
      const endDate = r.data?.current_period_end
        ? new Date(r.data.current_period_end * 1000).toLocaleDateString()
        : 'the end of your billing period';
      toast.success(`Canceled. You'll keep premium access until ${endDate}.`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel. Please try again.');
    } finally {
      setManaging(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setManaging(true);
    try {
      await api.post('/subscription/reactivate');
      toast.success('Reactivated! Your subscription will continue to renew.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reactivate. Please try again.');
    } finally {
      setManaging(false);
    }
  };

  const renewalDateText = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  if (loading) {
    return <div className="min-h-screen bg-stone-950 text-stone-400 flex items-center justify-center">Loading…</div>;
  }

  // Native iOS/Android build: never show prices or purchase CTAs (Apple Guideline 3.1.1).
  if (isNativeApp()) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 text-stone-100 p-4" data-testid="upgrade-page-native">
        <div className="max-w-md mx-auto space-y-6 pt-10">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-stone-400" data-testid="upgrade-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <Card className="bg-stone-900 border-stone-700">
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider">
                <Crown size={12} /> DIAMOND SCORER · PREMIUM
              </div>
              <p className="text-stone-300 text-sm leading-relaxed">
                Premium subscriptions are not available in this version of the app.
              </p>
              <p className="text-stone-500 text-xs leading-relaxed">
                Your Diamond Scorer account — including any existing premium access — syncs automatically when you sign in.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 text-stone-100 p-4" data-testid="upgrade-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-stone-400" data-testid="upgrade-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>

        {polling && (
          <Card className="bg-amber-900/30 border-amber-700 text-amber-200">
            <CardContent className="p-4 text-sm">⏳ Finalizing your subscription… this takes a few seconds.</CardContent>
          </Card>
        )}

        {/* Headline */}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider mb-3">
            <Crown size={12} /> DIAMOND SCORER · PREMIUM
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            UPGRADE YOUR GAME
          </h1>
          <p className="text-stone-400 mt-2 text-sm sm:text-base">
            Keep everything you love. Add the tools that win trophies.
          </p>
          {requestedFeature && (
            <p className="mt-3 inline-flex items-center gap-2 bg-stone-800 border border-amber-700/50 text-amber-300 text-xs px-3 py-1.5 rounded-full">
              <Sparkles size={12} /> Unlocks: <span className="font-bold">{requestedFeature}</span>
            </p>
          )}
        </div>

        {/* Current status */}
        {hidePlans ? (
          <Card className="bg-emerald-900/30 border-emerald-600 text-emerald-200" data-testid="premium-status-card">
            <CardContent className="p-4 text-center space-y-2">
              <p className="font-bold flex items-center justify-center gap-2"><Check size={16} /> {isAdmin ? "You're the admin — full access" : "You're on Premium"}</p>
              {!isAdmin && isRecurring && renewalDateText && !cancelAtPeriodEnd && (
                <>
                  <p className="text-xs">
                    {tier === 'premium_annual' ? 'Annual' : 'Monthly'} subscription · auto-renews on <b>{renewalDateText}</b>
                  </p>
                  <p className="text-[11px] text-emerald-100/80">
                    You will be charged <b>${tier === 'premium_annual' ? '59.00 / year' : '7.99 / month'}</b> automatically until you cancel below.
                  </p>
                </>
              )}
              {!isAdmin && isRecurring && cancelAtPeriodEnd && renewalDateText && (
                <p className="text-xs text-amber-300">
                  Canceling — premium access ends on <b>{renewalDateText}</b>. You won't be charged again.
                </p>
              )}
              {!isAdmin && !isRecurring && renewalDateText && (
                <p className="text-xs text-amber-300">
                  Legacy 30-day pass · expires <b>{renewalDateText}</b>. Upgrade to auto-renew to never lose access.
                </p>
              )}
              {!isAdmin && isRecurring && !cancelAtPeriodEnd && (
                <div className="pt-2">
                  <button
                    className="text-[11px] text-stone-300 hover:text-white underline-offset-2 hover:underline disabled:opacity-50"
                    onClick={handleCancelSubscription}
                    disabled={managing}
                    data-testid="cancel-subscription-btn"
                  >
                    {managing ? 'Working…' : 'Cancel subscription'}
                  </button>
                </div>
              )}
              {!isAdmin && isRecurring && cancelAtPeriodEnd && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReactivateSubscription}
                    disabled={managing}
                    className="border-emerald-500 text-emerald-200 hover:bg-emerald-900/40"
                    data-testid="reactivate-subscription-btn"
                  >
                    {managing ? 'Working…' : 'Reactivate subscription'}
                  </Button>
                </div>
              )}
              {subscriptionStatus === 'past_due' && (
                <p className="text-[11px] text-amber-300 bg-amber-950/30 rounded p-2 mt-2">
                  ⚠️ Last payment failed. Stripe will retry automatically. <a href="https://billing.stripe.com/p/login" target="_blank" rel="noreferrer" className="underline">Update payment method</a>.
                </p>
              )}
            </CardContent>
          </Card>
        ) : trialActive && (
          <Card className="bg-amber-900/30 border-amber-600 text-amber-200">
            <CardContent className="p-4 text-center">
              <p className="font-bold flex items-center justify-center gap-2"><Sparkles size={16} /> Free trial active</p>
              <p className="text-xs mt-1">
                {trialDaysLeft > 0
                  ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left. Lock in your plan before your trial ends.`
                  : `Your trial ends today. Pick a plan below to keep Premium.`}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Perks */}
        <Card className="bg-stone-900/60 border-stone-800">
          <CardContent className="p-5 space-y-2.5">
            {PREMIUM_PERKS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3 text-sm" data-testid={`perk-${i}`}>
                <Icon size={16} className="text-amber-400 shrink-0" />
                <span className="text-stone-200">{text}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Plans */}
        {!hidePlans && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {plans.map(plan => {
                const selected = selectedPlanId === plan.id;
                const isAnnual = plan.id === 'premium_annual';
                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={cn(
                      'relative p-4 rounded-xl text-left transition-all border-2',
                      selected
                        ? 'border-amber-500 bg-amber-950/40 ring-2 ring-amber-500/50'
                        : 'border-stone-800 bg-stone-900/60 hover:border-stone-700'
                    )}
                    data-testid={`plan-${plan.id}`}
                  >
                    {isAnnual && (
                      <span className="absolute -top-2.5 right-3 text-[9px] font-extrabold bg-emerald-500 text-white px-2 py-0.5 rounded-full tracking-wider">
                        SAVE $36
                      </span>
                    )}
                    <p className="text-[10px] uppercase text-stone-400 font-bold tracking-wider">{isAnnual ? 'Annual' : 'Monthly'}</p>
                    <p className="text-3xl font-extrabold text-white mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      ${plan.amount}
                    </p>
                    <p className="text-[11px] text-stone-400">{isAnnual ? 'per year' : 'per month'}</p>
                    {isAnnual && <p className="text-[10px] text-emerald-400 mt-1 font-semibold">$4.92 / month · billed yearly</p>}
                  </button>
                );
              })}
            </div>

            <Button
              className="w-full h-12 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-bold text-base shadow-lg"
              onClick={handleCheckout}
              disabled={checking || polling}
              data-testid="upgrade-checkout-btn"
            >
              {checking ? 'Starting checkout…' : `Continue with ${plans.find(p => p.id === selectedPlanId)?.name || 'Premium'}`}
            </Button>

            {/* Promo code */}
            <div className="bg-stone-900/60 border border-stone-800 rounded-lg p-3" data-testid="upgrade-promo-section">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 font-bold mb-2">Have a promo code?</p>
              <div className="flex gap-2">
                <input
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                  placeholder="LAUNCH50"
                  className={cn(
                    "flex-1 rounded-md border px-3 py-2 text-sm bg-stone-800 uppercase tracking-wider font-mono text-white",
                    promoStatus?.ok ? "border-emerald-500" : promoStatus?.ok === false ? "border-red-500" : "border-stone-700"
                  )}
                  data-testid="upgrade-promo-input"
                />
                <Button
                  variant="outline"
                  onClick={validatePromo}
                  disabled={!promoCode.trim()}
                  className="shrink-0 text-white border-stone-700 bg-stone-800 hover:bg-stone-700"
                  data-testid="upgrade-promo-apply"
                >
                  Apply
                </Button>
              </div>
              {promoStatus && (
                <p
                  className={cn("text-xs mt-2", promoStatus.ok ? "text-emerald-400 font-semibold" : "text-red-400")}
                  data-testid="upgrade-promo-status"
                >
                  {promoStatus.ok ? '✓ ' : ''}{promoStatus.summary}
                </p>
              )}
            </div>

            <p className="text-center text-[11px] text-stone-500">
              <Shield size={10} className="inline mr-1" />
              Secure payment via Stripe · Auto-renews monthly or yearly · Cancel anytime — no questions
            </p>
          </>
        )}

        <div className="text-center pt-2 pb-8">
          <button
            className="text-stone-500 hover:text-stone-300 text-xs underline"
            onClick={() => navigate('/home')}
            data-testid="upgrade-stay-free-btn"
          >
            Stay on the free plan — no thanks
          </button>

          {/* Legal footer — required for App Store paid-subscription compliance */}
          <div className="mt-6 flex items-center justify-center gap-3 text-[10px] text-stone-500">
            <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-stone-300 underline-offset-2 hover:underline" data-testid="upgrade-privacy-link">Privacy Policy</a>
            <span>·</span>
            <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-stone-300 underline-offset-2 hover:underline" data-testid="upgrade-terms-link">Terms of Use</a>
            <span>·</span>
            <a href="mailto:support@diamondscorer.com" className="hover:text-stone-300" data-testid="upgrade-support-link">Support</a>
          </div>
          <p className="text-[9px] text-stone-600 mt-2 max-w-md mx-auto leading-relaxed">
            Subscriptions automatically renew at the chosen interval (monthly or annually) using the card on file. Cancel anytime from this page or your <a href="https://billing.stripe.com/p/login" target="_blank" rel="noreferrer" className="underline">Stripe billing portal</a> — your access continues through the end of the current billing period and no further charges occur.
          </p>
        </div>
      </div>
    </div>
  );
}
